import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'

export interface UseFhirDeleteMutationOptions<T extends ResourceType> {
  mutation?: Omit<
    UseMutationOptions<ExtractResource<T>, unknown, ExtractResource<T>>,
    'mutationFn'
  >
}

/**
 * @param type - The resource type.
 * @param options - The options.
 * @returns The query result.
 * @deprecated Use useDeleteMutation instead
 */
export function useFhirDeleteMutation<T extends ResourceType>(
  type: T,
  options?: UseFhirDeleteMutationOptions<T>
): UseMutationResult<ExtractResource<T>, unknown, ExtractResource<T>> {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()

  return useMutation({
    ...options?.mutation,
    onSuccess: (_, variables, context) => {
      cacheKeys.invalidateMutation(queryClient, type, variables.id as string)
      options?.mutation?.onSuccess?.(_, variables, context)
    },
    mutationFn: (resource) => {
      if (isReadOnly) {
        console.warn(
          '[read-only] mutation attempted: useFhirDeleteMutation',
          type
        )
      }
      return medplum.deleteResource(type, resource.id as string)
    },
  })
}
